import { api } from './apiClient.js';

/**
 * List agents with pagination, sort, filters, and optional keyword search.
 *
 * Body shape (camelCase — matches POST /v1/workspace/agent/list contract):
 * ```json
 * {
 *   "page": 1,
 *   "pageSize": 20,
 *   "orderBy": "created_at",
 *   "orderDirection": "DESC",
 *   "filters": {
 *     "status": ["active"],
 *     "type": ["ai"],
 *     "category": ["Service"],
 *     "createdAt": { "from": "...", "to": "..." }
 *   },
 *   "search": { "query": "...", "fields": ["name"] }
 * }
 * ```
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const listAgents = (body) => api.post('/v1/workspace/agent/list', body);

/**
 * Fetch a single agent by ID.
 * GET /v1/workspace/agent/:id
 *
 * @param {string} id  Agent UUID
 * @returns {Promise<unknown>}
 */
export const getAgent = (id) => api.get(`/v1/workspace/agent/${id}`);

/**
 * Create agent.
 * POST /v1/workspace/agent/create
 *
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const createAgent = (body) => api.post('/v1/workspace/agent/create', body);

/**
 * Build ANN-compatible update payload for POST /v1/workspace/agent/update.
 * Keeps both starters representations:
 *  - root `starters`
 *  - `metadata.conversation_starters`
 *
 * @param {string} uuid
 * @param {Record<string, unknown>} body
 * @returns {Record<string, unknown>}
 */
export const buildUpdateAgentPayload = (uuid, body = {}) => ({
  uuid,
  name: body.name,
  description: body.description,
  tenant_id: body.tenant_id ?? body.tenantId,
  metadata: {
    ...(body.metadata && typeof body.metadata === 'object' ? body.metadata : {}),
    imageUrl: body.metadata?.imageUrl,
    conversation_starters: Array.isArray(body.starters) ? body.starters : [],
  },
  owner: body.owner,
  created_by: body.created_by ?? body.createdBy,
  status: body.status,
  type: body.type,
  version: body.version,
  scope: body.scope,
  tags: body.tags,
  category: body.category,
  starters: Array.isArray(body.starters) ? body.starters : [],
  image: body.image,
  signature: body.signature,
  model: body.model,
});

/**
 * Update agent.
 * POST /v1/workspace/agent/update
 *
 * @param {string} uuid
 * @param {Record<string, unknown>} body
 * @returns {Promise<unknown>}
 */
export const updateAgent = (uuid, body) =>
  api.post('/v1/workspace/agent/update', buildUpdateAgentPayload(uuid, body));

/**
 * Load the instruction linked to an agent (if any).
 * GET /v1/workspace/agent/:agentUuid/instruction
 *
 * @param {string} agentUuid
 * @returns {Promise<unknown>}
 */
export const getAgentInstruction = (agentUuid) =>
  api.get(`/v1/workspace/agent/${agentUuid}/instruction`);

/**
 * Associate an instruction UUID with an agent after create.
 * POST /v1/agents/:agentUuid/instruction
 *
 * @param {string} agentUuid
 * @param {string} instructionUuid
 * @returns {Promise<unknown>}
 */
export const addInstructionToAgent = (agentUuid, instructionUuid) =>
  api.post(`/v1/agents/${agentUuid}/instruction`, {
    instruction_uuid: instructionUuid,
  });

/**
 * Vaults/drives linked to an agent.
 * GET /v1/workspace/agent/:id/drives/list
 *
 * @param {string} agentUuid
 * @returns {Promise<unknown>}
 */
export const getDrivesByAgent = (agentUuid) =>
  api.get(`/v1/workspace/agent/${encodeURIComponent(agentUuid)}/drives/list`);

/**
 * Documents assigned to an agent (any vault).
 * GET /v1/workspace/agent/:id/documents/list
 *
 * @param {string} agentUuid
 * @returns {Promise<unknown>}
 */
export const getDocumentsByAgent = (agentUuid) =>
  api.get(`/v1/workspace/agent/${encodeURIComponent(agentUuid)}/documents/list`);
